import { withStyles } from '@mui/styles';
import React from 'react';
import { FocusOn } from 'react-focus-on';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { getCurrentConference } from '../../base/conference/functions';
import { IC_ROLES } from '../../base/conference/icRoles';
import { getLocalParticipant, getRemoteParticipants, isParticipantModerator } from '../../base/participants/functions';
import Button from '../../base/ui/components/web/Button';
import { isElementInTheViewport } from '../../base/ui/functions.web';
import { getParticipant, hideAssistancePanel, setParticipant } from './functions';
const styles = (theme) => {
    return {
        assisteesPanel: {
            maxWidth: '600px',
            height: 'auto',
            backgroundColor: theme.palette.ui01,
            position: 'absolute',
            bottom: 'calc(50% - 140px)',
            left: 'calc(50% - 225px)',
            padding: theme.spacing(3),
            borderRadius: '10px'
        },
        header: {
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(3)
        },
        inputblockContainer: {
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3)
        },
        inputDescription: {
            fontSize: '1rem',
            display: 'inline-grid',
            alignContent: 'center'
        },
        inputblock: {
            display: 'flex',
            justifyContent: 'space-between',
            gap: theme.spacing(3)
        },
        inputButton: {
            fontSize: '1rem',
            cursor: 'pointer',
            alignSelf: 'center',
            borderRadius: '20px',
            padding: '0.5rem 1rem !important'
        },
        closeButton: {
            position: 'absolute',
            top: '5px',
            right: '15px',
            fontSize: '1.5rem',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none'
        },
        headline: {
            fontSize: '1.5rem',
            color: theme.palette.text01,
            marginBottom: theme.spacing(3),
            marginTop: 0
        },
        description: {
            fontSize: '1rem',
            marginBottom: theme.spacing(3)
        },
        list: {
            listStyleType: 'none',
            paddingLeft: '0px'
        },
        participantEntry: {
            display: 'flex',
            flexDirection: 'row',
            marginBottom: '1rem'
        },
        participantName: {
            flexGrow: 1,
            fontSize: '1.2rem',
            color: theme.palette.text01,
            fontWeight: 500,
            alignSelf: 'center'
        },
        participantButton: {
            fontSize: '1.2rem',
            cursor: 'pointer',
            alignSelf: 'center',
            borderRadius: '30px',
            padding: '0.5rem 1rem !important'
        },
        focusLock: {
            zIndex: 1
        }
    };
};
const AssistancePanel = ({ classes, _conference, _localParticipant, _participant, _participantsList, _visible, _iAmAssisted, _iAmAssistant }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const _onClickClose = () => {
        dispatch(hideAssistancePanel());
    };
    const onClickRequest = (participant) => {
        dispatch(setParticipant(participant));
        _conference?.addLocalICRole(IC_ROLES.ASSISTED, participant?.id);
        dispatch(hideAssistancePanel());
    };
    const onClickRelease = () => {
        _conference?.removeLocalICRole(IC_ROLES.ASSISTED, _participant?.id);
        if (_localParticipant) {
            _conference?.removeICRole(_participant?.id, IC_ROLES.ASSISTANT, _localParticipant.id);
        }
        dispatch(hideAssistancePanel());
    };
    if (_visible) {
        if (!_iAmAssisted) {
            return (React.createElement(FocusOn, { className: classes.focusLock, returnFocus: 
                // If we return the focus to an element outside the viewport the page will scroll to
                // this element which in our case is undesirable and the element is outside of the
                // viewport on purpose (to be hidden). For example if we return the focus to the toolbox
                // when it is hidden the whole page will move up in order to show the toolbox. This is
                // usually followed up with displaying the toolbox (because now it is on focus) but
                // because of the animation the whole scenario looks like jumping large video.
                isElementInTheViewport },
                React.createElement("div", { "aria-modal": 'true', className: classes.assisteesPanel, id: 'assistancePanel', role: 'dialog' },
                    React.createElement("button", { className: classes.closeButton, 
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick: _onClickClose, role: 'button' }, "x"),
                    React.createElement("h1", { className: classes.headline, id: 'assistancePanelHeadline' }, t('assistancePanel.headline')),
                    React.createElement("p", { className: classes.description }, t('assistancePanel.desc1')),
                    React.createElement("p", { className: classes.description }, t('assistancePanel.desc2')),
                    React.createElement("div", { className: classes.inputblockContainer },
                        _participantsList.length > 0
                            && !_iAmAssistant
                            && _participantsList.map(participant => (React.createElement("div", { className: classes.inputblock, key: participant.id },
                                React.createElement("div", { className: classes.inputDescription }, participant.name),
                                React.createElement(Button, { accessibilityLabel: t('toolbar.accessibility.assistanceRequest'), className: classes.inputButton, label: t('toolbar.assistanceRequest'), 
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onClick: () => onClickRequest(participant) })))),
                        (_participantsList.length <= 0 || _iAmAssistant) && (React.createElement("div", { className: classes.inputblock },
                            React.createElement("div", { className: classes.inputDescription }, t('toolbar.assistanceParticipantsListEmpty'))))))));
        }
        return (React.createElement(FocusOn, { className: classes.focusLock, returnFocus: 
            // If we return the focus to an element outside the viewport the page will scroll to
            // this element which in our case is undesirable and the element is outside of the
            // viewport on purpose (to be hidden). For example if we return the focus to the toolbox
            // when it is hidden the whole page will move up in order to show the toolbox. This is
            // usually followed up with displaying the toolbox (because now it is on focus) but
            // because of the animation the whole scenario looks like jumping large video.
            isElementInTheViewport },
            React.createElement("div", { "aria-modal": 'true', className: classes.assisteesPanel, id: 'assistancePanel', role: 'dialog' },
                React.createElement("button", { className: classes.closeButton, 
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick: _onClickClose, role: 'button' }, "x"),
                React.createElement("h1", { className: classes.headline, id: 'assistancePanelHeadline' }, t('assistancePanel.headlineRelease')),
                React.createElement("p", { className: classes.description }, t('assistancePanel.descRelease')),
                React.createElement("button", { className: [classes.participantButton, 'primary'].join(' '), 
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick: onClickRelease }, t('assistancePanel.buttonRelease')))));
    }
    return React.createElement(React.Fragment, null);
};
const mapStateToProps = (state) => {
    const conference = getCurrentConference(state);
    const localParticipant = getLocalParticipant(state);
    const remoteParticipants = Array.from(getRemoteParticipants(state).values());
    const assistancePanelVisible = state['features/inklusiva/rolematching'].assistancePanelVisible;
    let amIAssisted = false;
    let amIAssistant = false;
    localParticipant?.icRoles?.forEach((role) => {
        if (role.name === IC_ROLES.ASSISTED) {
            amIAssisted = true;
        }
    });
    localParticipant?.icRoles?.forEach((role) => {
        if (role.name === IC_ROLES.ASSISTANT) {
            amIAssistant = true;
        }
    });
    const remoteParticipantsList = remoteParticipants // no local participants
        .filter((participant) => !conference?.checkMemberHasRole(participant.id, IC_ROLES.ASSISTANT)) // no assistants
        .filter((participant) => !conference?.checkMemberHasRole(participant.id, IC_ROLES.ASSISTED)) // no assistees
        .filter((participant) => !isParticipantModerator(participant)); // no moderators
    return {
        _conference: conference,
        _iAmAssistant: amIAssistant,
        _iAmAssisted: amIAssisted,
        _localParticipant: localParticipant,
        _participant: getParticipant(state),
        _participantsList: remoteParticipantsList,
        _visible: assistancePanelVisible
    };
};
export default withStyles(styles)(connect(mapStateToProps)(AssistancePanel));
